/**
 * Split monthly commentary for page-1 / page-2 PDF cards.
 *
 * Used by PdfPreviewDialog so each half is its own `wrap={false}` card (rounded
 * corners + page-2 top padding). Soft cut prefers paragraph breaks, sentence ends,
 * then clause tokens such as "weiterhin" when near the word budget.
 */
export const DEFAULT_COMMENTARY_FIRST_PART_WORDS = 175;

export function resolveCommentaryFirstPartWords(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : DEFAULT_COMMENTARY_FIRST_PART_WORDS;
}

function joinTokens(tokens: string[]): string {
  return tokens.join(' ').replace(/ ?\n ?/g, '\n').trim();
}

/** Soft break tokens that may end page 1 mid-sentence when near the word budget. */
function isSoftClauseBreak(token: string): boolean {
  return token === 'weiterhin' || /[.!?;:]["»)]?$/.test(token);
}

export function splitCommentary(text: string, maxWords: number): { first: string; second: string } {
  const source = typeof text === 'string' ? text : '';
  const tokens = source.replace(/\n/g, ' \n ').split(' ').filter((t) => t.length > 0);
  const wordCount = tokens.filter((t) => t !== '\n').length;
  if (wordCount <= maxWords) return { first: joinTokens(tokens), second: '' };

  let seen = 0;
  let limit = tokens.length;
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i] !== '\n') seen += 1;
    if (seen === maxWords) {
      limit = i + 1;
      break;
    }
  }

  const minimum = Math.max(1, Math.floor(limit * 0.6));
  let cut = limit;
  for (let i = limit; i >= minimum; i -= 1) {
    const token = tokens[i - 1];
    if (token === '\n' || isSoftClauseBreak(token)) {
      cut = i;
      break;
    }
  }
  return { first: joinTokens(tokens.slice(0, cut)), second: joinTokens(tokens.slice(cut)) };
}
